import React, { useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import { useSelector } from "react-redux";
import { selectCid } from "../../../features/companyid";
import { FaSpinner, FaDownload } from "react-icons/fa";
import { formatVariablesFromHTML } from "../../../utils/settings";
import { server_API } from "../../../config";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function RightEditorSectionOffer({
  quillBody = "",
  variableOptions = [],
  activeTab,
  setActiveTab,
  setFormData,
  formData,
}) {
  const [content, setContent] = useState(quillBody);
  const [showDropdown, setShowDropdown] = useState(false);
  const editorRef = useRef();
  const dropdownRef = useRef();
  const [loading, setLoading] = useState(false);
  const cid = useSelector(selectCid);
  const [pdf, setPdf] = useState(null);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setContent(quillBody);
  }, [quillBody]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleInsertVariable = (variable) => {
    const quill = editorRef.current.getEditor();
    const cursor = quill.getSelection(true);

    const variableHTML = `<span style="color:#2563eb;">&lt;${variable}&gt;</span>`;
    quill.clipboard.dangerouslyPasteHTML(cursor.index, variableHTML);

    quill.setSelection(cursor.index + variableHTML.length, 0);

    quill.format("color", false);

    setShowDropdown(false);
  };

  const handleDownload = () => {
    if (pdf) {
      const link = document.createElement("a");
      link.href = pdf;
      link.download = "offer_letter.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white pl-3 pb-4">
      <div className="flex gap-2 p-0.5 border justify-center w-fit mx-auto rounded bg-gray-100">
        <button
          onClick={() => setActiveTab("edit")}
          className={`py-2 px-10 font-medium w-fit rounded text-black ${activeTab === "edit" && "bg-white"}`}
          disabled={loading}
        >
          Edit
        </button>

        <button
          onClick={async () => {
            // Only generate PDF here, DO NOT SAVE
            const form = new FormData();
            form.append("offer_details", formatVariablesFromHTML(quillBody));
            form.append("companyid", cid);
            form.append("salarytemplateuuid", formData.mapped_salary);
            form.append("annualctc", 1000000)
            setLoading(true);
            await fetch(
              server_API + "/v2/template/generate-letter",
              {
                method: "POST",
                body: form,
              }
            )
              .then(async (res) => {
                const contentType = res.headers.get("content-type");

                if (contentType.includes("application/pdf")) {
                  const blob = await res.blob();
                  const blobUrl = URL.createObjectURL(blob);

                  const arrayBuffer = await blob.arrayBuffer();
                  const pdf = await pdfjs.getDocument({ data: arrayBuffer })
                    .promise;
                  const totalPages = pdf.numPages;
                  setTotalPages(totalPages);
                  setPdf(blobUrl);
                }

                setLoading(false);
                setActiveTab("preview");
              })
              .catch((err) => {
                setLoading(false);
                console.error("Error fetching PDF:", err);
              });
          }}
          disabled={loading || quillBody.length < 240 || !formData.mapped_salary}
          className={`py-2 px-10 font-medium w-fit rounded flex text-black items-center gap-2 ${activeTab === "preview" && "bg-white"
            }`}
        >
          Preview {loading && <FaSpinner className="animate-spin ml-2" />}
        </button>


      </div>

      <div className="relative flex-1 py-4">
        {activeTab === "edit" ? (
          <div className="relative">
            <div className="absolute right-2 top-[48px] z-10" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown((p) => !p)}
                className="border border-gray-300 bg-white rounded-md px-3 py-1 text-sm hover:bg-gray-50"
              >
                Add Var
              </button>

              {showDropdown && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 w-fit rounded-md shadow-md z-10 max-h-[200px] overflow-y-auto">
                  {variableOptions.map((variable) => (
                    <div
                      key={variable}
                      onClick={() => handleInsertVariable(variable)}
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer min-w-max w-full text-sm text-gray-700"
                    >
                      {variable}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <ReactQuill
              ref={editorRef}
              theme="snow"
              value={content}
              onChange={(value) => {
                setContent(value);
                setFormData((p) => ({ ...p, offer_body: value }));
              }}
              placeholder="Start writing..."
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ align: [] }],
                  ["clean"],
                ],
              }}
              className="h-[450px]"
            />
          </div>
        ) : pdf ? (
          <Document
            file={pdf}
            className={"h-[500px] w-[400px] overflow-y-scroll"}
          >
            {Array.from({ length: totalPages }, (_, index) => (
              <Page
                key={`page-${index + 1}`}
                pageNumber={index + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                height={500}
                width={350}
              />
            ))}
          </Document>
        ) : (
          <div className="flex items-center justify-center h-[400px] text-gray-500">
            No PDF available
          </div>
        )}
        {pdf && (
          <button
            onClick={handleDownload}
            className="absolute bottom-4 right-4 p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors z-20"
            title="Download PDF"
          >
            <FaDownload />
          </button>
        )}
      </div>
    </div>
  );
}
